import { useRouter } from "next/router";
import PropTypes from "prop-types";

import CheckCircleFilled from "@/icons/CheckCircleFilled";
import DraftIcon from "@/icons/DraftIcon";
import ArticleActivityPickerOption from "@/view/ArticleActivityPicker/ArticleActivityPickerOption";
import getRelevantWritePromptResponses from "@/view/utils/getRelevantWritePromptResponses";
import getRelevantWritePrompts from "../utils/getRelevantWritePrompts";
import {
  activityOptionLabelCss,
  draftIconCss,
  instructionsCss,
  listContainerCss,
  submittedIconCss,
} from "./WritePromptClassroomSelection.style";

const propTypes = {
  classrooms: PropTypes.arrayOf(PropTypes.object),
  collectionId: PropTypes.number,
  fetchedWritePrompts: PropTypes.arrayOf(
    PropTypes.shape({
      collections: PropTypes.arrayOf(PropTypes.object),
      id: PropTypes.string,
      teacher: PropTypes.object,
      questions: PropTypes.arrayOf(PropTypes.object),
    }),
  ),
  legacyArticleLevelId: PropTypes.number,
  responses: PropTypes.array,
};

const WritePromptClassroomSelection = ({
  classrooms,
  collectionId,
  fetchedWritePrompts,
  legacyArticleLevelId,
  responses,
}) => {
  const router = useRouter();
  const {
    pathname,
    query,
    query: { writePromptId, levelId },
  } = router;

  const handleClick = (href) => {
    router.replace(href, null, { shallow: true });
  };

  const getResponseStatus = (classroom) => {
    if (!responses || responses.length === 0) return;

    const prompts = getRelevantWritePrompts(
      legacyArticleLevelId,
      classroom,
      fetchedWritePrompts,
      responses,
      collectionId,
    );

    /**
     * Multiple prompts may be returned, but for now,
     * we're only displaying an indicator for the most relevant prompt
     */
    const mostRelevantPrompt = prompts?.[0];

    const relevantWriteResponses = getRelevantWritePromptResponses(
      responses,
      legacyArticleLevelId,
      mostRelevantPrompt?.id,
      classroom?.id,
    );

    if (relevantWriteResponses.length === 0) return;

    const mostRecentResponse =
      relevantWriteResponses?.[relevantWriteResponses?.length - 1];
    const responseHasRevisionRequest =
      mostRecentResponse?.review &&
      mostRecentResponse?.review?.date_revision_requested !== null;

    // We don't currently have an icon for revision requests, so we'll return null for now
    if (responseHasRevisionRequest) return;

    const status = mostRecentResponse.date_completed ? "submitted" : "draft";

    return status;
  };

  const responseIcon = {
    draft: {
      icon: DraftIcon,
      css: draftIconCss,
    },
    submitted: {
      icon: CheckCircleFilled,
      css: submittedIconCss,
    },
  };

  const eventProperties = {
    objectType: "WritePrompt",
    actionPrefix: "Initiate",
    actionName: "Navigate",
    streamProperties: {
      content_type: "Article",
      description: "Navigate to write prompt associated with classroom",
      object_id: writePromptId,
      content_id: levelId,
    },
  };

  const renderClassroomOptions = () => {
    if (!classrooms?.length) {
      return null;
    }

    return (
      <ul role="list" css={listContainerCss}>
        {classrooms.map((classroom) => {
          const href = {
            pathname,
            query: {
              ...query,
              classroomId: classroom.id,
            },
          };

          const responseStatus = getResponseStatus(classroom);
          const statusIndicator = responseIcon[responseStatus];
          const Icon = statusIndicator?.icon;
          const iconStyles = statusIndicator?.css;
          const completedLabel = statusIndicator ? "completed" : "";

          return (
            <ArticleActivityPickerOption
              ariaProps={{
                "aria-label": `${classroom?.name}, ${completedLabel}`,
              }}
              key={classroom.id}
              eventProperties={eventProperties}
              onClick={() => handleClick(href)}
            >
              <span css={activityOptionLabelCss}>{classroom?.name}</span>
              {statusIndicator && <Icon css={iconStyles} />}
            </ArticleActivityPickerOption>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <h3 css={instructionsCss}>Select a class to respond to:</h3>
      {renderClassroomOptions()}
    </>
  );
};

WritePromptClassroomSelection.propTypes = propTypes;
export default WritePromptClassroomSelection;
